import React, { useEffect, useState } from 'react';

import { GetProp, Modal } from 'antd';
import styled from 'styled-components';

import { CommunityContract } from 'types/contracts';

import { useContractContext } from '../../../common/Contract/ContractContext';
import { COMMUNITY_CONTRACT_TYPE } from '../../../constants';
import Button from '../../lib/Button';
import Checkbox from '../../lib/Checkbox';
import { testIdFormattedLabel } from '../testIdUtils';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StyledModal = styled(Modal)`
  &&& {
    .ant-modal-content {
      padding: 0px;
      line-height: 1;
      border-radius: 1px;
      user-select: none;
    }
    .ant-modal-body {
      padding: 0px;
      line-height: normal;
    }
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.span`
  font-size: 20px;
  font-family: var(--font-bold);
  color: var(--text-primary);
  padding: 30px 30px 0px 30px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const Info = styled.span`
  display: flex;
  flex-direction: column;
  font-size: 14px;
  color: var(--text-secondary);
  padding: 0px 30px 20px 30px;
`;

const Selection = styled.div`
  border-top: 1px solid var(--line-gray);
  padding: 20px 30px 80px 30px;
`;

const Label = styled.div`
  display: flex;
  justify-content: space-between;
  &.note {
    color: var(--text-secondary);
  }
`;

const Footer = styled.div`
  display: flex;
  border-top: 1px solid var(--line-gray);
  padding: 20px 30px;
  gap: 10px;
  justify-content: space-between;
`;

const FooterRow = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
`;

const StyledCheckbox = styled(Checkbox)`
  .ant-checkbox + span {
    font-size: 14px;
    width: 100%;

    .note {
      font-size: 12px;
      color: var(--text-secondary);
    }
  }
`;

const OrangeLabel = styled.div`
  color: var(--negative-orange) !important;
`;

const ConsentBox = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
`;

const ConsentLabel = styled.div`
  font-size: 12px;
  width: 141px;
`;

const ConsentCheckbox = styled(Checkbox)`
  & .ant-checkbox + span {
    font-size: 12px;
    overflow: hidden;
    white-space: nowrap;
    max-width: 136px;
    text-overflow: ellipsis;
    box-sizing: border-box;
  }
`;

interface Props {
  open: boolean;
  initialSelection?: string[];
  contractOptions: CommunityContract[];
  onClose: () => void;
  onConfirm: (selection: string[]) => void;
}

const ShopDataModalContractsModal = ({ open, contractOptions, initialSelection = [], onConfirm, onClose }: Props) => {
  const { keys: existingContractGroupSlugs } = useContractContext();
  const [selection, setSelection] = useState<string[]>(initialSelection);
  const [consentGiven, setConsentGiven] = useState(false);

  useEffect(() => {
    if (initialSelection.length) {
      setSelection(initialSelection);
    }
  }, [initialSelection]);

  const handleClose = () => {
    setSelection([]);
    onClose();
    setConsentGiven(false);
  };
  const handleChange: GetProp<typeof Checkbox.Group, 'onChange'> = (checkedValues: any[]) => {
    setSelection(checkedValues.map(String));
  };

  const handleConfirm = () => {
    onConfirm(selection);
    handleClose();
    setConsentGiven(false);
  };

  const showConsent = initialSelection.length > 0 && selection.length === 0;

  return (
    <StyledModal open={open} footer={null} destroyOnClose onCancel={handleClose}>
      <Container>
        <Header>
          <Title>{initialSelection.length > 0 ? 'Edit Contract Type(s)' : 'Add Contract Type(s)'}</Title>
        </Header>
        <Content>
          <Info>
            {initialSelection.length > 0
              ? 'Unselect all contract types to delete the entire contract type set and all entered data.'
              : 'Please add one or a group of contract types to enter a set of rates for. A contract type that does not appear here already has data in another set.'}
          </Info>
          <Selection>
            <Checkbox.Group
              style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
              value={selection}
              onChange={handleChange}
            >
              {contractOptions
                .filter(
                  (contract) =>
                    initialSelection.length ||
                    !existingContractGroupSlugs.some((slug) => slug.split('|').some((s) => s === contract.slug))
                )
                .map((option, index) => {
                  return (
                    <StyledCheckbox className={'green'} key={option.slug} value={option.slug} >
                      <Label>
                        <span style={{ display: 'flex', gap: 4 }} data-test-id={`${testIdFormattedLabel(option.name)}-${index}`}>
                          <span>{option.name}</span>
                          {option.type !== COMMUNITY_CONTRACT_TYPE.RENTAL && <span>{option.refund_percentage}%</span>}
                        </span>
                      </Label>
                    </StyledCheckbox>
                  );
                })}
            </Checkbox.Group>
          </Selection>
        </Content>
      </Container>
      <Footer>
        <div>
          {showConsent && (
            <ConsentBox>
              <ConsentCheckbox
                className="green"
                checked={consentGiven}
                onChange={(e: any) => setConsentGiven(e.target.checked)}
              />
              <ConsentLabel>I consent to deleting data specified above</ConsentLabel>
            </ConsentBox>
          )}
        </div>
        <FooterRow>
          <Button
            type={'primary'}
            disabled={(!selection.length && !initialSelection.length) || (showConsent && !consentGiven)}
            style={{ fontFamily: 'var(--font-bold)', height: 40, width: 120 }}
            onClick={handleConfirm}
          >
            {initialSelection.length > 0 ? 'Save' : 'Add'}
          </Button>
          <Button style={{ fontFamily: 'var(--font-bold)', height: 40, width: 120 }} onClick={handleClose}>
            Cancel
          </Button>
        </FooterRow>
      </Footer>
    </StyledModal>
  );
};

export default ShopDataModalContractsModal;
